import type {
	PricingEstimationCostBreakdown,
	PricingEstimationState,
	PricingEstimationType,
	PricingEstimationTypeRange,
} from '../../types';
import { isMonthSeasonActive } from './season';

/**
 * Map a list of raw estimation sources (each either a `PricingEstimationState`
 * already in shape, or a `PricingEstimationCostBreakdown` that needs season
 * resolution) to a uniform array of `PricingEstimationState`s. The caller is
 * expected to pipe the result into `prioritizePricingEstimationValues` for the
 * final merged state.
 */
export const transformCostBreakdownElementsToPricingEstimations = (
	pricingEstimations?:
		| (PricingEstimationState | PricingEstimationCostBreakdown)[]
		| null,
	month?: number | null,
): PricingEstimationState[] => {
	if (!pricingEstimations || pricingEstimations.length === 0) return [];

	const peakMonths = firstMonthsField(pricingEstimations, 'peakMonths');
	const offPeakMonths = firstMonthsField(pricingEstimations, 'offPeakMonths');

	const isPeakSeason = isMonthSeasonActive(month, peakMonths);
	const isOffPeakSeason = isMonthSeasonActive(month, offPeakMonths);

	const mapped = pricingEstimations
		.map((estimation): PricingEstimationState | null => {
			if ('peak' in estimation && 'offPeak' in estimation) {
				return transformCostBreakdownToPricingEstimation(
					estimation,
					isPeakSeason,
					isOffPeakSeason,
				);
			}
			return estimation;
		})
		.filter(
			(estimation): estimation is PricingEstimationState => estimation !== null,
		);

	return mapped;
};

const firstMonthsField = (
	pricingEstimations: (
		| PricingEstimationState
		| PricingEstimationCostBreakdown
	)[],
	field: 'peakMonths' | 'offPeakMonths',
): string[] | null => {
	for (const estimation of pricingEstimations) {
		const value = estimation[field];
		if (value) return value;
	}
	return null;
};

/**
 * Resolve a single `PricingEstimationCostBreakdown` into a
 * `PricingEstimationState` by selecting peak / off-peak values based on the
 * month, or — when the month is outside both seasons — collapsing peak and
 * off-peak into a `PricingEstimationTypeRange` via `buildRangeValue`.
 */
const transformCostBreakdownToPricingEstimation = (
	costBreakdown: PricingEstimationCostBreakdown | null,
	isPeakSeason: boolean,
	isOffPeakSeason: boolean,
): PricingEstimationState | null => {
	if (!costBreakdown) return null;

	const { peak, offPeak } = costBreakdown;
	const isPeakNotDefined = isPeakSeason === isOffPeakSeason;

	const ceremonyValue: PricingEstimationType = isPeakNotDefined
		? buildRangeValue(offPeak.ceremony, peak.ceremony)
		: isPeakSeason
			? costBreakdown.peak.ceremony
			: costBreakdown.offPeak.ceremony;

	const receptionValue: PricingEstimationType = isPeakNotDefined
		? buildRangeValue(offPeak.reception, peak.reception)
		: isPeakSeason
			? peak.reception
			: offPeak.reception;

	const cateringValue: PricingEstimationType = isPeakNotDefined
		? buildRangeValue(offPeak.catering, peak.catering)
		: isPeakSeason
			? peak.catering
			: offPeak.catering;

	const barServicesValue: PricingEstimationType = isPeakNotDefined
		? buildRangeValue(offPeak.barServices, peak.barServices)
		: isPeakSeason
			? peak.barServices
			: offPeak.barServices;

	return {
		ceremony: ceremonyValue,
		reception: receptionValue,
		catering: cateringValue,
		barServices: barServicesValue,
		peakMonths: costBreakdown.peakMonths,
		offPeakMonths: costBreakdown.offPeakMonths,
	};
};

const buildRangeValue = (
	offPeakType: PricingEstimationType | null | undefined,
	peakType: PricingEstimationType | null | undefined,
): PricingEstimationType => {
	const toNumberArray = (...values: (number | null | undefined)[]): number[] =>
		values.filter((v): v is number => typeof v === 'number');

	const minCandidates = toNumberArray(
		offPeakType?.cost,
		offPeakType?.minCost,
		peakType?.cost,
		peakType?.minCost,
	);

	const maxCandidates = toNumberArray(
		peakType?.cost,
		peakType?.maxCost,
		offPeakType?.cost,
		offPeakType?.maxCost,
	);

	// If we have no numeric pricing information at all, do not construct a range.
	if (minCandidates.length === 0 && maxCandidates.length === 0) {
		return null;
	}

	// At this point we know we have at least one numeric candidate.
	const allCandidates = [...minCandidates, ...maxCandidates];

	const minCost = Math.min(...allCandidates);
	const maxCost = Math.max(...allCandidates);

	if (minCost === maxCost) {
		return {
			cost: minCost,
			currency: offPeakType?.currency ?? peakType?.currency,
			unit: offPeakType?.unit ?? peakType?.unit,
			dataSource: offPeakType?.dataSource ?? peakType?.dataSource,
			isFree: offPeakType?.isFree || peakType?.isFree,
		};
	}

	const range: PricingEstimationTypeRange = {
		currency: offPeakType?.currency ?? peakType?.currency,
		minCost,
		maxCost,
		unit: offPeakType?.unit ?? peakType?.unit,
		dataSource: offPeakType?.dataSource ?? peakType?.dataSource,
		isFree: offPeakType?.isFree || peakType?.isFree,
	};
	return range;
};

/**
 * Merge a sequence of `PricingEstimationState`s into a single state by taking
 * the first non-null value per category. Used to combine FAQ and AI sources,
 * with the FAQ-derived state first in the array so it takes priority on
 * categories it provides.
 */
export const prioritizePricingEstimationValues = (
	pricingEstimations?: PricingEstimationState[] | null,
): PricingEstimationState => {
	const empty: PricingEstimationState = {
		ceremony: null,
		reception: null,
		catering: null,
		barServices: null,
		peakMonths: null,
		offPeakMonths: null,
	};

	if (!pricingEstimations || pricingEstimations.length === 0) return empty;

	let pricingState: PricingEstimationState = pricingEstimations[0] ?? empty;
	for (const state of pricingEstimations) {
		pricingState = mergePricingEstimations(pricingState, state);
	}

	return pricingState;
};

const mergePricingEstimations = (
	mainPricingEstimation: PricingEstimationState | null,
	secondaryPricingEstimation: PricingEstimationState | null,
): PricingEstimationState => {
	if (!mainPricingEstimation && !secondaryPricingEstimation) {
		return {
			ceremony: null,
			reception: null,
			catering: null,
			barServices: null,
			peakMonths: null,
			offPeakMonths: null,
		};
	}

	if (!mainPricingEstimation) {
		return { ...secondaryPricingEstimation };
	}

	if (!secondaryPricingEstimation) {
		return { ...mainPricingEstimation };
	}

	const mergedEstimation: PricingEstimationState = {
		...mainPricingEstimation,
	};

	const shouldUseFallbackValue = (
		mainValue: PricingEstimationType,
		fallbackValue: PricingEstimationType,
	): boolean => {
		if (!fallbackValue) return false;
		if (!mainValue) return true;

		const isNonFreeZeroFixedPrice =
			!mainValue.isFree &&
			'cost' in mainValue &&
			typeof mainValue.cost === 'number' &&
			mainValue.cost === 0;

		return isNonFreeZeroFixedPrice;
	};

	if (
		shouldUseFallbackValue(
			mergedEstimation.ceremony,
			secondaryPricingEstimation.ceremony,
		)
	) {
		mergedEstimation.ceremony = secondaryPricingEstimation.ceremony;
	}
	if (
		shouldUseFallbackValue(
			mergedEstimation.reception,
			secondaryPricingEstimation.reception,
		)
	) {
		mergedEstimation.reception = secondaryPricingEstimation.reception;
	}
	if (
		shouldUseFallbackValue(
			mergedEstimation.catering,
			secondaryPricingEstimation.catering,
		)
	) {
		mergedEstimation.catering = secondaryPricingEstimation.catering;
	}
	if (
		shouldUseFallbackValue(
			mergedEstimation.barServices,
			secondaryPricingEstimation.barServices,
		)
	) {
		mergedEstimation.barServices = secondaryPricingEstimation.barServices;
	}
	if (!mergedEstimation.peakMonths && secondaryPricingEstimation.peakMonths) {
		mergedEstimation.peakMonths = secondaryPricingEstimation.peakMonths;
	}
	if (
		!mergedEstimation.offPeakMonths &&
		secondaryPricingEstimation.offPeakMonths
	) {
		mergedEstimation.offPeakMonths = secondaryPricingEstimation.offPeakMonths;
	}

	return mergedEstimation;
};
